import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass, Trash, PencilSimple, ArrowClockwise, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import MessagingDialog from "@/components/MessagingDialog";

/**
 * 2026-02 (iter120) — Entwürfe-Übersicht.
 *
 * Listet alle offenen Message-Drafts des angemeldeten Nutzers und lässt sie
 * per Klick im MessagingDialog fortsetzen oder gezielt löschen.
 */
export default function Entwuerfe() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openDraft, setOpenDraft] = useState(null); // { draft_id, contact_id?, tenancy_id?, property_id? }

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (showAll) params.include_all_users = true;
      const { data } = await api.get("/messages/drafts", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Entwürfe konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showAll]);

  const filtered = items.filter((d) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      (d.subject || "").toLowerCase().includes(needle)
      || (d.sub_subject || "").toLowerCase().includes(needle)
      || (d.category || "").toLowerCase().includes(needle)
    );
  });

  const handleDelete = async (draftId) => {
    if (!await window._domivusConfirm("Diesen Entwurf endgültig löschen?")) return;
    try {
      await api.delete(`/messages/drafts/${draftId}`);
      toast.success("Entwurf gelöscht");
      load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const handleOpen = (d) => {
    setOpenDraft({
      draft_id: d.id,
      contact_id: d.contact_id,
      tenancy_id: d.tenancy_id,
      property_id: d.property_id,
      unit_id: d.unit_id,
      vorgang_id: d.vorgang_id,
    });
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const dt = new Date(iso);
      return dt.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="entwuerfe-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Entwürfe</h1>
          <p className="text-sm text-slate-500 mt-1">
            Nicht versendete Schreiben — werden automatisch beim Bearbeiten gespeichert.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="gap-2" data-testid="entwuerfe-refresh">
            <ArrowClockwise size={14} /> Neu laden
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Suche in Betreff, Unterbetreff, Kategorie…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9"
            data-testid="entwuerfe-search"
          />
        </div>
        <label className="text-xs text-slate-600 flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            data-testid="entwuerfe-show-all"
          />
          Alle Entwürfe der Kanzlei zeigen
        </label>
        <div className="text-xs text-slate-500 ml-auto">
          {loading ? "Lade…" : `${filtered.length} Entwurf(e)`}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Betreff</th>
              <th className="text-left px-3 py-2 font-semibold">Kategorie</th>
              <th className="text-left px-3 py-2 font-semibold">Empfänger</th>
              <th className="text-left px-3 py-2 font-semibold">Zuletzt gespeichert</th>
              <th className="text-right px-3 py-2 font-semibold">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-slate-400">
                  <FileText size={28} className="mx-auto mb-2 text-slate-300" />
                  {loading ? "Lade…" : "Keine Entwürfe."}
                </td>
              </tr>
            )}
            {filtered.map((d) => (
              <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`entwurf-row-${d.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-800 line-clamp-1">
                    {(d.subject || "").trim() || <span className="italic text-slate-400">(kein Betreff)</span>}
                  </div>
                  {d.sub_subject && (
                    <div className="text-[11px] text-slate-500 line-clamp-1">{d.sub_subject}</div>
                  )}
                </td>
                <td className="px-3 py-2">
                  {d.category && <Badge variant="outline" className="text-[10px]">{d.category}</Badge>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {(d.recipient_keys || []).length > 0
                    ? `${d.recipient_keys.length} Empfänger`
                    : d.email_to || <span className="italic text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500 tabular-nums whitespace-nowrap">
                  {fmtDate(d.updated_at || d.created_at)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1.5"
                      onClick={() => handleOpen(d)}
                      data-testid={`entwurf-open-${d.id}`}
                    >
                      <PencilSimple size={12} /> Öffnen
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      onClick={() => handleDelete(d.id)}
                      data-testid={`entwurf-delete-${d.id}`}
                      title="Entwurf löschen"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openDraft && (
        <MessagingDialog
          open={!!openDraft}
          onOpenChange={(v) => { if (!v) { setOpenDraft(null); load(); } }}
          context={openDraft}
        />
      )}
    </div>
  );
}
